import React, { useEffect, useRef, useState } from 'react';
import { RotateCw, RefreshCw, ZoomIn, ZoomOut, Maximize, Sun, HelpCircle, X, Sparkles, Box } from 'lucide-react';

const THREE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
const ORBIT_CONTROLS_CDN = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js';
const GLTF_LOADER_CDN = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js';
const DRACO_LOADER_CDN = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/DRACOLoader.js';

const Web3DViewer = ({ glbUrl, usdzUrl, productName = 'ساعة يد إمبراطورية فاخرة', scaleFactor = 1.0, lightingPreset = 'studio' }) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [autoRotate, setAutoRotate] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [lighting, setLighting] = useState(lightingPreset || 'studio');
    const [showQr, setShowQr] = useState(false);

    // References to Three.js elements for controls
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const controlsRef = useRef(null);
    const modelRef = useRef(null);
    const handsRef = useRef({ hour: null, minute: null, second: null });
    const lightsRef = useRef([]);

    // Load Three.js scripts sequentially from CDN
    useEffect(() => {
        let isMounted = true;
        const loadThreeScripts = async () => {
            try {
                if (!window.THREE) await loadScript(THREE_CDN);
                if (!window.THREE.OrbitControls) await loadScript(ORBIT_CONTROLS_CDN);
                if (!window.THREE.GLTFLoader) await loadScript(GLTF_LOADER_CDN);
                if (!window.THREE.DRACOLoader) await loadScript(DRACO_LOADER_CDN);
                if (isMounted) setScriptsLoaded(true);
            } catch (err) {
                console.error("Failed to load Three.js from CDN", err);
                if (isMounted) setScriptsLoaded(true);
            }
        };
        loadThreeScripts();
        return () => { isMounted = false; };
    }, []);

    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                if (existing.getAttribute('data-loaded') === 'true') return resolve();
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
                return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => {
                script.setAttribute('data-loaded', 'true');
                resolve();
            };
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    };

    // Helper to build procedural 3D Luxury Swiss Chronograph
    const buildProceduralLuxuryModel = (THREE, scene, name) => {
        const rootGroup = new THREE.Group();
        rootGroup.name = "LuxuryChronograph";

        // ================= 1. LUXURY BASE & PEDESTAL =================
        const plinthGroup = new THREE.Group();
        
        // Tier 1: Brushed Gold Octagonal Rim
        const baseGeom = new THREE.CylinderGeometry(1.6, 1.7, 0.08, 64);
        const goldMat = new THREE.MeshStandardMaterial({
            color: 0xD4AF37,
            metalness: 0.92,
            roughness: 0.18
        });
        const baseMesh = new THREE.Mesh(baseGeom, goldMat);
        baseMesh.position.y = -0.04;
        baseMesh.receiveShadow = true;
        plinthGroup.add(baseMesh);

        // Tier 2: Dark Obsidian / Black Marble Mirror Plinth
        const pedestalGeom = new THREE.CylinderGeometry(1.5, 1.55, 0.14, 64);
        const marbleMat = new THREE.MeshStandardMaterial({
            color: 0x090D16,
            roughness: 0.08,
            metalness: 0.85
        });
        const pedestal = new THREE.Mesh(pedestalGeom, marbleMat);
        pedestal.position.y = 0.07;
        pedestal.receiveShadow = true;
        pedestal.castShadow = true;
        plinthGroup.add(pedestal);

        // Top Gold Trim Ring
        const ringGeom = new THREE.RingGeometry(1.45, 1.5, 64);
        ringGeom.rotateX(-Math.PI / 2);
        const ringMat = new THREE.MeshStandardMaterial({
            color: 0xF3E5AB,
            metalness: 0.95,
            roughness: 0.1,
            side: THREE.DoubleSide
        });
        const ring = new THREE.Mesh(ringGeom, ringMat);
        ring.position.y = 0.141;
        plinthGroup.add(ring);

        // Luxury Velvet Display Cushion / Stand
        const cushionGeom = new THREE.CylinderGeometry(0.7, 0.78, 0.55, 32);
        const velvetMat = new THREE.MeshStandardMaterial({
            color: 0x111827,
            roughness: 0.85,
            metalness: 0.1
        });
        const cushion = new THREE.Mesh(cushionGeom, velvetMat);
        cushion.position.set(0, 0.4, -0.05);
        cushion.rotation.x = Math.PI * 0.18; // Tilted toward viewer
        cushion.castShadow = true;
        cushion.receiveShadow = true;
        plinthGroup.add(cushion);

        rootGroup.add(plinthGroup);

        // ================= 2. 18K GOLD WATCH CASE =================
        const watchGroup = new THREE.Group();
        watchGroup.position.set(0, 0.62, 0.08);
        watchGroup.rotation.x = Math.PI * 0.18; // Match cushion tilt

        // Watch Middle Case (18k Gold)
        const caseGeom = new THREE.CylinderGeometry(0.72, 0.72, 0.18, 64);
        const watchCase = new THREE.Mesh(caseGeom, goldMat);
        watchCase.castShadow = true;
        watchCase.receiveShadow = true;
        watchGroup.add(watchCase);

        // Fluted Bezel Ring with teeth
        const bezelGeom = new THREE.TorusGeometry(0.7, 0.05, 16, 64);
        bezelGeom.rotateX(Math.PI / 2);
        const bezel = new THREE.Mesh(bezelGeom, goldMat);
        bezel.position.y = 0.09;
        watchGroup.add(bezel);

        // ================= 3. SUNBURST DIAL FACE =================
        const dialGeom = new THREE.CylinderGeometry(0.66, 0.66, 0.02, 64);
        const dialMat = new THREE.MeshStandardMaterial({
            color: 0x05131A, // Deep Emerald / Royal Midnight Blue
            roughness: 0.15,
            metalness: 0.7
        });
        const dial = new THREE.Mesh(dialGeom, dialMat);
        dial.position.y = 0.088;
        watchGroup.add(dial);

        // Dial Gold Hour Markers (12 Indices)
        const markerGeom = new THREE.BoxGeometry(0.025, 0.015, 0.1);
        const markerMat = new THREE.MeshStandardMaterial({
            color: 0xFFD700,
            metalness: 0.98,
            roughness: 0.1
        });
        for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            const marker = new THREE.Mesh(markerGeom, markerMat);
            const radius = 0.52;
            marker.position.set(Math.sin(angle) * radius, 0.1, Math.cos(angle) * radius);
            marker.rotation.y = angle;
            watchGroup.add(marker);
        }

        // Subdials (3 Chronograph sub-circles at 3, 6, 9 o'clock)
        const subPositions = [
            { x: 0.24, z: 0 },
            { x: -0.24, z: 0 },
            { x: 0, z: -0.24 }
        ];
        const subGeom = new THREE.RingGeometry(0.12, 0.14, 32);
        subGeom.rotateX(-Math.PI / 2);
        subPositions.forEach(pos => {
            const subRing = new THREE.Mesh(subGeom, markerMat);
            subRing.position.set(pos.x, 0.101, pos.z);
            watchGroup.add(subRing);
        });

        // 3D Embossed LuxVono Crown Logo on Dial
        const logoGeom = new THREE.CylinderGeometry(0.05, 0.05, 0.01, 16);
        const logoMesh = new THREE.Mesh(logoGeom, markerMat);
        logoMesh.position.set(0, 0.102, 0.22);
        watchGroup.add(logoMesh);

        // ================= 4. REAL-TIME TICKING HANDS =================
        const handMat = new THREE.MeshStandardMaterial({ color: 0xFFE082, metalness: 0.98, roughness: 0.05 });
        const secMat = new THREE.MeshStandardMaterial({ color: 0xD4AF37, metalness: 0.95, roughness: 0.1 });

        // Hour Hand
        const hourGeom = new THREE.BoxGeometry(0.035, 0.015, 0.28);
        hourGeom.translate(0, 0, 0.12);
        const hourHand = new THREE.Mesh(hourGeom, handMat);
        hourHand.position.y = 0.11;
        watchGroup.add(hourHand);

        // Minute Hand
        const minGeom = new THREE.BoxGeometry(0.025, 0.015, 0.42);
        minGeom.translate(0, 0, 0.18);
        const minHand = new THREE.Mesh(minGeom, handMat);
        minHand.position.y = 0.12;
        minHand.rotation.y = Math.PI * 0.4;
        watchGroup.add(minHand);

        // Sweeping Seconds Hand
        const secGeom = new THREE.BoxGeometry(0.012, 0.015, 0.52);
        secGeom.translate(0, 0, 0.2);
        const secHand = new THREE.Mesh(secGeom, secMat);
        secHand.position.y = 0.13;
        watchGroup.add(secHand);

        // Center Pin
        const pinGeom = new THREE.CylinderGeometry(0.035, 0.035, 0.04, 16);
        const pin = new THREE.Mesh(pinGeom, markerMat);
        pin.position.y = 0.125;
        watchGroup.add(pin);

        handsRef.current = { hour: hourHand, minute: minHand, second: secHand };

        // ================= 5. SAPPHIRE CRYSTAL GLASS =================
        const crystalGeom = new THREE.CylinderGeometry(0.68, 0.68, 0.04, 64);
        const crystalMat = new THREE.MeshPhysicalMaterial({
            color: 0xFFFFFF,
            transparent: true,
            opacity: 0.4,
            transmission: 0.92,
            roughness: 0.03,
            ior: 1.54,
            reflectivity: 0.95,
            clearcoat: 1.0,
            clearcoatRoughness: 0.05
        });
        const crystal = new THREE.Mesh(crystalGeom, crystalMat);
        crystal.position.y = 0.13;
        watchGroup.add(crystal);

        // ================= 6. CROWN & CHRONO PUSHERS =================
        const crownGeom = new THREE.CylinderGeometry(0.08, 0.08, 0.12, 24);
        crownGeom.rotateZ(Math.PI / 2);
        const crown = new THREE.Mesh(crownGeom, goldMat);
        crown.position.set(0.78, 0, 0);
        watchGroup.add(crown);

        const pusherGeom = new THREE.CylinderGeometry(0.05, 0.05, 0.09, 16);
        pusherGeom.rotateZ(Math.PI / 2);
        const pusherTop = new THREE.Mesh(pusherGeom, goldMat);
        pusherTop.position.set(0.72, 0, 0.35);
        watchGroup.add(pusherTop);

        const pusherBottom = new THREE.Mesh(pusherGeom, goldMat);
        pusherBottom.position.set(0.72, 0, -0.35);
        watchGroup.add(pusherBottom);

        // ================= 7. 3-LINK SOLID GOLD BRACELET =================
        const linkCount = 20;
        const linkGeom = new THREE.BoxGeometry(0.38, 0.08, 0.14);
        const linkSideGeom = new THREE.BoxGeometry(0.12, 0.07, 0.14);

        for (let i = 0; i < linkCount; i++) {
            const angle = (i / linkCount) * Math.PI * 2;
            const radiusX = 0.68;
            const radiusY = 0.52;
            
            const linkMesh = new THREE.Mesh(linkGeom, goldMat);
            linkMesh.position.set(0, Math.sin(angle) * radiusY, Math.cos(angle) * radiusX);
            linkMesh.rotation.x = -angle;
            watchGroup.add(linkMesh);

            // Side links with high shine
            const leftLink = new THREE.Mesh(linkSideGeom, goldMat);
            leftLink.position.set(0.26, Math.sin(angle) * radiusY, Math.cos(angle) * radiusX);
            leftLink.rotation.x = -angle;
            watchGroup.add(leftLink);

            const rightLink = new THREE.Mesh(linkSideGeom, goldMat);
            rightLink.position.set(-0.26, Math.sin(angle) * radiusY, Math.cos(angle) * radiusX);
            rightLink.rotation.x = -angle;
            watchGroup.add(rightLink);
        }

        rootGroup.add(watchGroup);
        scene.add(rootGroup);
        modelRef.current = rootGroup;
        setLoading(false);
    };

    // Initialize Scene, Camera, Renderer, and Controls
    useEffect(() => {
        if (!scriptsLoaded || !canvasRef.current || !containerRef.current) return;

        const THREE = window.THREE;
        if (!THREE) return;

        const width = containerRef.current.clientWidth || 600;
        const height = containerRef.current.clientHeight || 450;

        // Scene
        const scene = new THREE.Scene();
        sceneRef.current = scene;
        scene.background = new THREE.Color('#070B14'); // Luxury Deep Noir Studio

        // Camera
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
        camera.position.set(0, 1.8, 3.4);
        cameraRef.current = camera;

        // Renderer
        const renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance'
        });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.25;
        rendererRef.current = renderer;

        // Controls
        const controls = new THREE.OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.06;
        controls.maxPolarAngle = Math.PI / 2 + 0.05; // Don't go below ground level
        controls.minDistance = 1.0;
        controls.maxDistance = 8.0;
        controls.target.set(0, 0.55, 0);
        controlsRef.current = controls;

        // Setup Lighting
        setupLights(THREE, scene, lighting);

        // Check if external GLB is valid & provided
        const hasValidGlb = glbUrl && typeof glbUrl === 'string' && glbUrl.trim().length > 4 && !glbUrl.includes('undefined');

        if (hasValidGlb && THREE.GLTFLoader) {
            const dracoLoader = new THREE.DRACOLoader();
            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.4.1/');

            const gltfLoader = new THREE.GLTFLoader();
            gltfLoader.setDRACOLoader(dracoLoader);

            const modelUrl = glbUrl.startsWith('http') || glbUrl.startsWith('/media')
                ? glbUrl
                : `https://media.luxvono.com${glbUrl.startsWith('/') ? '' : '/'}${glbUrl}`;

            gltfLoader.load(
                modelUrl,
                (gltf) => {
                    const model = gltf.scene;
                    modelRef.current = model;

                    const scale = Number(scaleFactor) || 1.0;
                    model.scale.set(scale, scale, scale);

                    model.traverse((child) => {
                        if (child.isMesh) {
                            child.castShadow = true;
                            child.receiveShadow = true;
                        }
                    });

                    const box = new THREE.Box3().setFromObject(model);
                    const center = box.getCenter(new THREE.Vector3());
                    const size = box.getSize(new THREE.Vector3());

                    model.position.x += (model.position.x - center.x);
                    model.position.y += (model.position.y - box.min.y) + 0.1;
                    model.position.z += (model.position.z - center.z);

                    scene.add(model);
                    setLoading(false);
                },
                (xhr) => {
                    if (xhr.total > 0) setProgress(Math.round((xhr.loaded / xhr.total) * 100));
                },
                (error) => {
                    console.warn("External GLB not found, generating procedural 3D model:", error);
                    buildProceduralLuxuryModel(THREE, scene, productName);
                }
            );
        } else {
            // Instantly render high-fidelity procedural 3D luxury model
            buildProceduralLuxuryModel(THREE, scene, productName);
        }

        // Animation Loop
        let animationFrameId;
        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);

            // Auto rotation of model
            if (autoRotate && modelRef.current) {
                modelRef.current.rotation.y += 0.006;
            }

            // Real-time ticking of seconds & minute hands
            if (handsRef.current.second) {
                handsRef.current.second.rotation.y -= 0.015;
            }
            if (handsRef.current.minute) {
                handsRef.current.minute.rotation.y -= 0.00025;
            }

            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        // Resize handler
        const handleResize = () => {
            if (!containerRef.current || !renderer || !camera) return;
            const w = containerRef.current.clientWidth;
            const h = containerRef.current.clientHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        };
        window.addEventListener('resize', handleResize);

        // Clean up
        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResize);
            if (rendererRef.current) rendererRef.current.dispose();
        };
    }, [scriptsLoaded, glbUrl, scaleFactor]);

    // Setup lights helper
    const setupLights = (THREE, scene, preset) => {
        lightsRef.current.forEach(light => scene.remove(light));
        lightsRef.current = [];

        const lights = [];

        // Ambient Light
        const ambient = new THREE.AmbientLight(preset === 'neon' ? '#1E1B4B' : '#FFFFFF', preset === 'sunset' ? 0.7 : 0.85);
        scene.add(ambient);
        lights.push(ambient);

        // Key Studio Spotlight (Gold / Daylight)
        const keyLight = new THREE.DirectionalLight(
            preset === 'sunset' ? '#FFB703' : (preset === 'neon' ? '#38BDF8' : '#FFFDF0'),
            1.8
        );
        keyLight.position.set(3, 5, 4);
        keyLight.castShadow = true;
        keyLight.shadow.mapSize.width = 2048;
        keyLight.shadow.mapSize.height = 2048;
        keyLight.shadow.bias = -0.0001;
        scene.add(keyLight);
        lights.push(keyLight);

        // Rim Light (Backlight for edge highlights)
        const rimLight = new THREE.DirectionalLight(
            preset === 'neon' ? '#F43F5E' : (preset === 'sunset' ? '#FB8500' : '#D4AF37'),
            1.2
        );
        rimLight.position.set(-3, 3, -3);
        scene.add(rimLight);
        lights.push(rimLight);

        // Fill Soft Light
        const fillLight = new THREE.PointLight(preset === 'neon' ? '#A855F7' : '#D4AF37', 0.8, 12);
        fillLight.position.set(0, -1, 3);
        scene.add(fillLight);
        lights.push(fillLight);

        lightsRef.current = lights;
    };

    // Handle lighting change
    useEffect(() => {
        if (!sceneRef.current || !window.THREE) return;
        setupLights(window.THREE, sceneRef.current, lighting);
    }, [lighting]);

    // Zoom In
    const zoomIn = () => {
        if (cameraRef.current && controlsRef.current) {
            cameraRef.current.position.multiplyScalar(0.85);
            controlsRef.current.update();
        }
    };

    // Zoom Out
    const zoomOut = () => {
        if (cameraRef.current && controlsRef.current) {
            cameraRef.current.position.multiplyScalar(1.18);
            controlsRef.current.update();
        }
    };

    // Reset view
    const resetView = () => {
        if (controlsRef.current && cameraRef.current && modelRef.current) {
            modelRef.current.rotation.set(0, 0, 0);
            cameraRef.current.position.set(0, 1.8, 3.4);
            controlsRef.current.target.set(0, 0.55, 0);
            controlsRef.current.update();
        }
    };

    // Launch AR
    const handleLaunchAR = () => {
        const isAndroid = /android/i.test(navigator.userAgent);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isAndroid && glbUrl) {
            const modelUrl = glbUrl.startsWith('http') ? glbUrl : `https://media.luxvono.com${glbUrl.startsWith('/') ? '' : '/'}${glbUrl}`;
            const fallbackUrl = window.location.href;
            const intentUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(modelUrl)}&mode=ar_only&title=${encodeURIComponent(productName || 'Luxvono 3D Product')}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end;`;
            window.location.href = intentUrl;
        } else if (isIOS && usdzUrl) {
            const uUrl = usdzUrl.startsWith('http') ? usdzUrl : `https://media.luxvono.com${usdzUrl.startsWith('/') ? '' : '/'}${usdzUrl}`;
            const link = document.createElement('a');
            link.setAttribute('rel', 'ar');
            const img = document.createElement('img');
            link.appendChild(img);
            link.setAttribute('href', uUrl);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            setShowQr(true);
        }
    };

    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(err => console.error(err));
        } else {
            document.exitFullscreen().then(() => setIsFullscreen(false));
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    return (
        <div 
            ref={containerRef} 
            className={`web-3d-viewer-container ${isFullscreen ? 'fullscreen' : ''}`}
            style={{
                position: isFullscreen ? 'fixed' : 'relative',
                top: isFullscreen ? 0 : 'auto',
                left: isFullscreen ? 0 : 'auto',
                width: isFullscreen ? '100vw' : '100%',
                height: isFullscreen ? '100vh' : '100%',
                minHeight: '440px',
                zIndex: isFullscreen ? 99999 : 1,
                background: 'radial-gradient(circle at center, #111827 0%, #060911 100%)',
                borderRadius: isFullscreen ? '0' : '20px',
                overflow: 'hidden',
                border: isFullscreen ? 'none' : '1px solid rgba(212, 175, 55, 0.25)',
                boxShadow: 'inset 0 0 60px rgba(0,0,0,0.85)'
            }}
        >
            <style>
                {`
                    .web-3d-overlay-button {
                        background: rgba(15, 23, 42, 0.85);
                        border: 1px solid rgba(212, 175, 55, 0.3);
                        color: #E2E8F0;
                        border-radius: 12px;
                        width: 38px;
                        height: 38px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        backdrop-filter: blur(12px);
                        box-shadow: 0 6px 16px rgba(0,0,0,0.4);
                    }
                    .web-3d-overlay-button:hover {
                        border-color: #D4AF37;
                        color: #D4AF37;
                        background: rgba(30, 41, 59, 0.95);
                        transform: scale(1.08);
                    }
                    .web-3d-overlay-button.active {
                        background: linear-gradient(135deg, #D4AF37, #AA771C);
                        color: #0B0F19;
                        border-color: #D4AF37;
                        box-shadow: 0 0 15px rgba(212, 175, 55, 0.5);
                    }
                    .ar-launch-pill {
                        background: linear-gradient(135deg, #D4AF37, #B8860B);
                        color: #0B0F19;
                        border: 1.5px solid rgba(255, 255, 255, 0.25);
                        border-radius: 30px;
                        padding: 10px 24px;
                        font-weight: 800;
                        font-size: 13.5px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        cursor: pointer;
                        transition: all 0.25s ease;
                        box-shadow: 0 8px 24px rgba(212, 175, 55, 0.35);
                        z-index: 10;
                    }
                    .ar-launch-pill:hover {
                        transform: translateY(-2px) scale(1.03);
                        box-shadow: 0 12px 30px rgba(212, 175, 55, 0.55);
                    }
                `}
            </style>

            {/* Three.js Canvas */}
            <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />

            {/* Loading Indicator */}
            {loading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(7, 11, 20, 0.92)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 20
                }}>
                    <div style={{
                        width: '56px',
                        height: '56px',
                        border: '3px solid rgba(212, 175, 55, 0.2)',
                        borderTop: '3px solid #D4AF37',
                        borderRadius: '50%',
                        animation: 'spin3D 1s linear infinite'
                    }} />
                    <style>{`@keyframes spin3D { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    <div style={{ marginTop: '16px', fontWeight: '800', color: '#F8FAFC', fontSize: '14px' }}>
                        جاري تجهيز المجسم ثلاثي الأبعاد 3D...
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#D4AF37', fontWeight: '700' }}>
                        محرك Three.js WebGL الملكي
                    </div>
                </div>
            )}

            {/* Control Sidebar overlay */}
            <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 10 }}>
                <button type="button" className="web-3d-overlay-button" onClick={toggleFullscreen} title="ملء الشاشة (Fullscreen)">
                    <Maximize size={16} />
                </button>
                <button type="button" className="web-3d-overlay-button" onClick={zoomIn} title="تكبير (Zoom In)">
                    <ZoomIn size={16} />
                </button>
                <button type="button" className="web-3d-overlay-button" onClick={zoomOut} title="تصغير (Zoom Out)">
                    <ZoomOut size={16} />
                </button>
                <button type="button" className={`web-3d-overlay-button ${autoRotate ? 'active' : ''}`} onClick={() => setAutoRotate(!autoRotate)} title="الدوران التلقائي (Auto Rotate)">
                    <RotateCw size={16} />
                </button>
                <button type="button" className="web-3d-overlay-button" onClick={resetView} title="إعادة ضبط الزاوية (Reset View)">
                    <RefreshCw size={16} />
                </button>
                <button
                    type="button"
                    className={`web-3d-overlay-button ${lighting !== 'studio' ? 'active' : ''}`}
                    onClick={() => {
                        const modes = ['studio', 'sunset', 'neon'];
                        const next = modes[(modes.indexOf(lighting) + 1) % modes.length];
                        setLighting(next);
                    }}
                    title="تغيير نمط الإضاءة (Studio / Sunset / Neon)"
                >
                    <Sun size={16} />
                </button>
            </div>

            {/* Interactive Hotspot / Help Prompt overlay */}
            <div style={{
                position: 'absolute',
                bottom: '16px',
                left: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                zIndex: 10,
                background: 'rgba(15, 23, 42, 0.8)',
                padding: '8px 14px',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                fontSize: '11.5px',
                color: '#E2E8F0',
                border: '1px solid rgba(212, 175, 55, 0.25)'
            }}>
                <Sparkles size={14} color="#D4AF37" />
                <span>اسحب للتدوير 360° • عجلة الفأرة للتكبير والتصغير</span>
            </div>

            {/* Bottom Centered AR launch pill */}
            <div style={{ position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                <button type="button" className="ar-launch-pill" onClick={handleLaunchAR}>
                    <Box size={16} />
                    <span>تجربة الواقع المعزز (AR in Your Room)</span>
                </button>
            </div>

            {/* Desktop QR Modal for AR scanning */}
            {showQr && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(7, 11, 20, 0.88)',
                    backdropFilter: 'blur(14px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 30
                }}>
                    <div style={{
                        background: '#0F172A',
                        padding: '26px',
                        borderRadius: '24px',
                        maxWidth: '340px',
                        textAlign: 'center',
                        position: 'relative',
                        border: '1.5px solid rgba(212, 175, 55, 0.4)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.8)'
                    }}>
                        <button type="button" onClick={() => setShowQr(false)} style={{ position: 'absolute', top: '14px', right: '14px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' }}>
                            <X size={20} />
                        </button>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '800', color: '#F8FAFC' }}>
                            ✨ المسح بالجوال لتجربة 3D AR
                        </h3>
                        <p style={{ fontSize: '11.5px', color: '#94A3B8', lineHeight: 1.6, marginBottom: '18px' }}>
                            وجه كاميرا هاتفك الذكي نحو الرمز أدناه لمعاينة المنتج في غرفتك بالواقع المعزز فوراً.
                        </p>
                        
                        <div style={{ background: '#FFFFFF', padding: '14px', borderRadius: '16px', display: 'inline-block', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                            <img 
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(window.location.href)}&color=0B0F19`}
                                alt="AR QR Code"
                                style={{ display: 'block', width: '170px', height: '170px' }}
                            />
                        </div>
                        
                        <div style={{ marginTop: '14px', fontSize: '11px', color: '#D4AF37', fontWeight: 700 }}>
                            يدعم كافة هواتف iPhone (QuickLook) و Android (ARCore)
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Web3DViewer;
